import React, { useEffect, useState } from 'react';
import useDeviceType from "../../hooks/useDeviceType";
import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";
import GlobalConst from "../../constants/GlobalConst";
import { getIso31662dbCountries } from "../../dynamic-packages";
import "./AddressForm.style.scss";

const allowedCountries = ['US', 'CA', 'MX'];

const defaultByTld = {
    '.mx': { country: 'Mexico',        countryCode: 'MX' },
    '.com': { country: 'United States', countryCode: 'US' },
    '.fr': { country: 'France',         countryCode: 'FR' },
};

const AddressForm = ({ vm, showSubmitButton }) => {
    const { isMobile } = useDeviceType();
    const [searchParams] = useSearchParams();
    const isEditAddress = searchParams.get('editAddress') === 'true';
    const [isoDb, setIsoDb] = useState(null);

    useEffect(() => {
        getIso31662dbCountries().then(db => {
            db.countries = db.countries.filter(c => allowedCountries.includes(c.iso));
            setIsoDb(db);
        });
    }, []);

    // Set default country once isoDb is loaded and no country is set yet
    useEffect(() => {
        if (!isoDb) return;
        const addr = vm.userData?.address;
        if (!addr?.country && !addr?.countryCode) {
            const defaults = defaultByTld[GlobalConst.SITE_TLD];
            if (defaults) vm.setAddressFields(defaults);
        }
    }, [isoDb]);

    useEffect(() => {
        if (isEditAddress) {
            document.getElementById('address1')?.focus();
        }
    }, [isEditAddress]);

    const addr = vm.userData?.address ?? {};

    const selectedCountryIso =
        isoDb?.countries.find(
            c => c.iso === addr.countryCode || c.name.toLowerCase() === (addr.country ?? '').toLowerCase()
        )?.iso ?? '';

    const regions = selectedCountryIso ? isoDb?.listOfCountries?.[selectedCountryIso]?.regions ?? [] : [];

    const selectedRegionIso =
        regions.find(
            r => r.iso === addr.stateCode || r.name.toLowerCase() === (addr.state ?? '').toLowerCase()
        )?.iso ?? '';

    const handleCountryChange = (e) => {
        const selected = isoDb?.countries.find(c => c.iso === e.target.value);
        if (!selected) return;
        vm.setAddressFields({
            country: selected.name,
            countryCode: selected.iso,
            state: '',
            stateCode: '',
        });
    };

    const handleStateChange = (e) => {
        const selected = regions.find(r => r.iso === e.target.value);
        if (!selected) return;
        vm.setAddressFields({
            state: selected.name,
            stateCode: selected.iso,
        });
    };

    return (
        <div className="AddressForm">
            <div className="addressFormTitle">
                <FormattedMessage id={"myAccountView.myProfile.addressTitle"} />
            </div>
            <form onSubmit={vm.handleAddressSubmit}>

                <div className="inputGroup">
                    <label htmlFor="address1">
                        <FormattedMessage id={"myAccountView.myProfile.streetAddress"} />
                    </label>
                    <input
                        type="text"
                        name="address1"
                        id="address1"
                        value={addr.address1 ?? ''}
                        onChange={vm.handleAddressChange}
                        className={vm.addressErrors.address ? 'inputError' : ''}
                    />
                    {vm.addressErrors.address && <div className="errorText">{vm.addressErrors.address}</div>}
                </div>

                <div className="inputGroup">
                    <label htmlFor="city">
                        <FormattedMessage id={"myAccountView.myProfile.city"} />
                    </label>
                    <input
                        type="text"
                        name="city"
                        value={addr.city ?? ''}
                        onChange={vm.handleAddressChange}
                        className={vm.addressErrors.city ? 'inputError' : ''}
                    />
                    {vm.addressErrors.city && <div className="errorText">{vm.addressErrors.city}</div>}
                </div>

                <div className="inputGroup">
                    <label htmlFor="country">
                        <FormattedMessage id={"myAccountView.myProfile.country"} />
                    </label>
                    <select
                        value={selectedCountryIso}
                        onChange={handleCountryChange}
                        className={vm.addressErrors.country ? 'inputError' : ''}
                    >
                        <option value="" disabled />
                        {isoDb?.countries.map(c => (
                            <option key={c.iso} value={c.iso}>{c.name}</option>
                        ))}
                    </select>
                    {vm.addressErrors.country && <div className="errorText">{vm.addressErrors.country}</div>}
                </div>

                {regions.length > 0 && (
                    <div className="inputGroup">
                        <label htmlFor="state">
                            <FormattedMessage id={"myAccountView.myProfile.state"} />
                        </label>
                        <select
                            value={selectedRegionIso}
                            onChange={handleStateChange}
                            className={vm.addressErrors.state ? 'inputError' : ''}
                        >
                            <option value="" disabled />
                            {regions.map(r => (
                                <option key={r.iso} value={r.iso}>{r.name}</option>
                            ))}
                        </select>
                        {vm.addressErrors.state && <div className="errorText">{vm.addressErrors.state}</div>}
                    </div>
                )}

                <div className="inputGroup">
                    <label htmlFor="zipCode">
                        <FormattedMessage id={"myAccountView.myProfile.zipCode"} />
                    </label>
                    <input
                        type="text"
                        name="zipCode"
                        value={addr.zipCode ?? ''}
                        onChange={vm.handleAddressChange}
                        className={vm.addressErrors.zipCode ? 'inputError' : ''}
                    />
                    {vm.addressErrors.zipCode && <div className="errorText">{vm.addressErrors.zipCode}</div>}
                </div>

                <div className="formGroup">
                    <label className="label" htmlFor="phone1">
                        <FormattedMessage id={"myAccountView.myProfile.phone"} />
                    </label>
                    <input
                        type="text"
                        name="phone1"
                        value={addr.phone1 ?? ''}
                        onChange={vm.handleAddressChange}
                        className={vm.addressErrors.phone ? 'inputError' : ''}
                    />
                    {vm.addressErrors.phone && <div className="errorText">*Phone Number Required</div>}
                </div>

                {showSubmitButton && (
                    <button className="submitBtn" type="submit" onClick={vm.handleAddressSubmit}>
                        Submit
                    </button>
                )}
            </form>
        </div>
    );
};

export default AddressForm;
